import { createTheme } from '@mui/material/styles';
import { COLOR } from '../constants/designTokens';

/**
 * Thème MUI de StaffDesk — **projection** de `src/constants/designTokens.js`
 * (source de vérité du charter, note vault [[UI]]). Ce fichier n'introduit
 * aucune nouvelle valeur : il mappe les tokens sur l'API MUI pour que les
 * composants MUI (checkbox, switch, tabs, menus, dialogs, tooltips, boutons…)
 * rendent nativement au look StaffDesk.
 *
 * Décisions (plan `theme-mui-uniformisation-ui` §5-6) :
 * - zéro redesign : tailles des variantes Typography inchangées, pas de CssBaseline ;
 * - `cssVariables: true` (v9) — tokens exposés en CSS vars, dark mode trivial le jour voulu ;
 * - `shape.borderRadius` 12 ; dialogs 16 (`rounded-2xl`), menus/popovers 12, boutons 8 (`rounded-lg` charter) ;
 * - `MuiButton` : sentence case (textTransform none), flat, Satoshi, hauteurs 32/40 (charter sm/md).
 */
const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: {
      main: COLOR.brand,
      dark: COLOR.brandActive,
      light: COLOR.brandSoft,
      contrastText: COLOR.inkInverse,
    },
    secondary: { main: COLOR.brandSoft, dark: COLOR.brand, contrastText: COLOR.inkInverse },
    error: { main: COLOR.danger, dark: COLOR.dangerText, contrastText: COLOR.inkInverse },
    warning: { main: COLOR.warning, dark: COLOR.warningText, contrastText: COLOR.ink },
    success: { main: COLOR.success, dark: COLOR.successText, contrastText: COLOR.inkInverse },
    info: { main: COLOR.brandSoft, dark: COLOR.brand, contrastText: COLOR.inkInverse },
    text: { primary: COLOR.ink, secondary: COLOR.inkMuted, disabled: COLOR.inkDisabled },
    divider: COLOR.line,
    background: { default: COLOR.surface, paper: COLOR.surface },
    action: { disabled: COLOR.inkDisabled, hover: COLOR.surfaceSubtle, selected: COLOR.brandTint },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: 'Satoshi, sans-serif',
    button: { textTransform: 'none', fontWeight: 500, fontSize: 14 },
  },
  components: {
    // Boutons — charter §Controls : flat, sentence case, rounded-lg (8px), 32/40px.
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: 'Satoshi, sans-serif',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: { padding: '0 12px', height: 32 },
        sizeMedium: { padding: '0 20px', height: 40 },
        text: {
          color: COLOR.ink,
          '&:hover': { backgroundColor: COLOR.surfaceSubtle },
        },
        outlined: {
          color: COLOR.ink,
          borderColor: COLOR.lineStrong,
          '&:hover': {
            borderColor: COLOR.inkDisabled,
            backgroundColor: COLOR.surfaceSubtle,
          },
        },
      },
    },
    // Dialogs — charter §Dialogs : panneau `rounded-2xl` (16px).
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
    // Menus & popovers — radius 12, aligné sur les paper existants des dropdowns custom.
    MuiMenu: { styleOverrides: { paper: { borderRadius: 12 } } },
    MuiPopover: { styleOverrides: { paper: { borderRadius: 12 } } },
    // Tooltip — ink (les tooltips custom gardent leurs overrides).
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: COLOR.ink, fontSize: 12, borderRadius: 8 },
        arrow: { color: COLOR.ink },
      },
    },
    // Tabs — underline brand-soft (charter §Tabs : TAB_CLASS 2px brand-soft).
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: COLOR.brandSoft } } },
    // DataGrid — MUI X dérive son radius du shape (×2 → 24px avec notre shape 12).
    // Les tables doivent rester quasi carrées : on fige la var à 4px.
    MuiDataGrid: {
      styleOverrides: {
        root: { '--unstable_DataGrid-radius': '4px' },
      },
    },
    MuiChip: { styleOverrides: { root: { fontFamily: 'Satoshi, sans-serif' } } },
  },
});

export default theme;
